import Phaser from 'phaser';
import { PreloadScene } from './scenes/PreloadScene';
import { MenuScene } from './scenes/MenuScene';
import { GameScene } from './scenes/GameScene';
import { StatsScene } from './scenes/StatsScene';
import { DebugMazeScene } from './scenes/DebugMazeScene';
import { VIEWPORT_PX_W, VIEWPORT_PX_H, COLORS } from './config';
import { ParentBridge } from './bridge/ParentBridge';
import { isWebGLAvailable, showWebGLError } from './boot/webgl-check';

// Gate the entire boot on WebGL availability. The Canvas 2D fallback that
// Phaser.AUTO would normally fall through to runs the game's timing-critical
// systems on the CPU, and the cadence of scroll / spider patrols / water
// animation degrades enough that the gameplay is meaningfully different from
// the intended experience. Rather than ship that worse-than-broken state, we
// detect the missing capability up front and show a clear explanation.
if (!isWebGLAvailable()) {
  showWebGLError();
} else {
  // Begin listening for parent SET_MUTE / SET_PAUSE messages before the game
  // boots, so commands sent during load are honored once scenes register.
  ParentBridge.start();

  const config: Phaser.Types.Core.GameConfig = {
    // Phaser.WEBGL (not AUTO) so Phaser won't silently fall back to Canvas 2D
    // even if WebGL context creation fails AFTER our pre-flight check passes.
    // If that ever happens we let Phaser throw, and the try/catch below shows
    // the same error UI.
    type: Phaser.WEBGL,
    parent: 'game',
    backgroundColor: COLORS.bg,
    width: VIEWPORT_PX_W,
    height: VIEWPORT_PX_H,
    pixelArt: true,
    roundPixels: true,
    scale: {
      mode: Phaser.Scale.FIT,
      // Center horizontally only. We deliberately do NOT center vertically:
      // CENTER_BOTH splits letterbox space into equal top+bottom margins, and on
      // short phone viewports the top margin can push the bottom of the game
      // off-screen. Pinning to the top keeps the whole playfield visible, with
      // any leftover space falling harmlessly below.
      autoCenter: Phaser.Scale.CENTER_HORIZONTALLY,
    },
    // Boot order: Preload (assets) -> Menu -> Game / Debug
    scene: [PreloadScene, MenuScene, GameScene, StatsScene, DebugMazeScene],
  };

  try {
    new Phaser.Game(config);
  } catch (err) {
    console.error('splat-chimp: Phaser failed to initialize WebGL renderer', err);
    showWebGLError();
  }
}

